<script setup>
import {computed, ref} from "vue";
import SuggestionBox from "@/components/ai-onboarding/partials/SuggestionBox.vue";
import InputWithButton from "@/components/global-themed/inputs/InputWithButton.vue";
import axios from "axios";
import LoadingBlock from "@/components/global-themed/LoadingBlock.vue";
import {useMutation, useQuery, useQueryClient} from "@tanstack/vue-query";
import {changeStep, Steps} from "@/composables/ai-onboarding/useSteps";
import NavigationButtons from "@/components/ai-onboarding/partials/NavigationButtons.vue";
import useWebsite from "@/composables/useWebsite";
import {simpleUrl} from "@/customValidation";
import SweetToast from "@/SweetToast";

const maximumSelectionLimit = 5;

const emit = defineEmits(['changeStep']);

const manualCompetitors = ref([]);
const selectedCompetitors = ref(new Set());

const { website } = useWebsite();

const selectionLimit = computed(() => {
  if (!website.value) {
    return 0;
  }

   return Math.min(website.value.competitionCredits, maximumSelectionLimit);
});

const {data: competitorData, isLoading} = useQuery({
  queryKey: ['competitor-suggestions'],
  queryFn: async () => {
    const {data} = await axios.get('/ajax/v2/ai-onboarding/competitor-suggestions');

    return data.data;
  },
});

const suggestedCompetitors = computed(() => competitorData.value?.suggestedCompetitors);
const previouslySelectedCompetitors = computed(() => competitorData.value?.competitors);
const inputWithButton = ref(null);
const competitorInput = ref('');

const {mutate: addCompetitor, isPending} = useMutation({
  mutationFn: async (domain) => {
    // Strip http/https and www. from the domain
    domain = domain.replace(/^(https?:\/\/)?(www\.)?/, "")
    const alreadySuggested = suggestedCompetitors.value.some(
      (competitor) => competitor.domain === domain
    );

    // if suggested but not already selected - return will hit onSuccess and will select it
    if (alreadySuggested && !selectedCompetitors.value.has(domain)) {
      return {domain, alreadySuggested};
    }

    if (
      manualCompetitors.value.includes(domain)
      || selectedCompetitors.value.has(domain)
      || previouslySelectedCompetitors.value.includes(domain))
    {
        inputWithButton.value.setErrors('The competitor is already in the list below');

      setTimeout(() => {
        inputWithButton.value.setErrors(null);
      }, 3000);

      throw new Error('Competitor already exists');
    }

    await axios.post('/ajax/v2/ai-onboarding/competitor/validate', {
      competitor: domain,
    });

    return {domain, alreadySuggested};
  },
  onSuccess: ({domain, alreadySuggested: isAlreadySuggested}) => {
    if (!isAlreadySuggested) {
      manualCompetitors.value.push(domain);
    }

    if (selectedCompetitors.value.size >= selectionLimit.value) {
      return;
    }

    toggleChecked(domain, true);
  },
  onError: (error) => {
    if (error.response.status !== 422) {
      throw new Error('Something went wrong');
    }

    SweetToast.fire({
      icon: 'info',
      title: "Competitor couldn't be added",
      text: "Competitor couldn't be added as it already exists, but its data will still be used for keyword suggestions",
    });
  },
});

const toggleChecked = (competitor, checked) => {
  if (checked) {
    selectedCompetitors.value.add(competitor);

    return;
  }

  selectedCompetitors.value.delete(competitor);
}

const queryClient = useQueryClient();

const {mutate: handleContinue, isPending: loading} = useMutation({
  mutationFn: async () => await axios.post('/ajax/v2/ai-onboarding/competitors/store', {
    competitors: Array.from(selectedCompetitors.value),
  }),
  onSuccess: () => {
    queryClient.invalidateQueries(['competitor-suggestions']);
    queryClient.invalidateQueries(['current-website']);

    changeStep(Steps.SERVICES_OFFERED, emit);
  },
  onError: () => {
    throw new Error('Something went wrong');
  },
});

const handleBack = () => {
  queryClient.invalidateQueries(['competitor-suggestions']);
  queryClient.invalidateQueries(['current-website']);

  changeStep(Steps.BUSINESS_DESCRIPTION, emit);
}

const tooltipText = computed(() => {
  return `You can only select ${selectionLimit.value} ${selectionLimit.value === 1 ? 'competitor' : 'competitors'}`
});

const totalSelectedCompetitorCount = computed(() => selectedCompetitors.value.size + previouslySelectedCompetitors.value?.length);
</script>

<template>
  <div class="tw-flex tw-flex-col tw-w-full tw-justify-between tw-h-full">
    <div class="tw-p-18 tw-text-center tw-overflow-y-auto">
      <div class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-y-6">
        <div class="tw-flex tw-flex-col tw-gap-y-12 tw-w-full tw-max-w-xl">
          <LoadingBlock v-if="isLoading">
            Loading suggestions
          </LoadingBlock>

          <div
            v-else
            class="tw-flex tw-flex-col tw-w-full tw-gap-4"
          >
            <label
              for="city"
              class="tw-self-start tw-text-lg tw-text-grey-dark tw-font-bold"
            >
              {{ suggestedCompetitors.length < 1 ? 'Add competitors:' : 'Add competitors, or select from the suggestions:' }}
            </label>

            <InputWithButton
              ref="inputWithButton"
              v-model="competitorInput"
              :validation-schema="simpleUrl"
              :loading="isPending"
              @submit="addCompetitor(competitorInput)"
            />
          </div>

          <div
            class="tw-flex tw-flex-col tw-gap-4"
          >
            <span
              v-if="totalSelectedCompetitorCount"
              class="tw-text-font-secondary tw-text-base tw-self-end"
            >
              Competitor count: <span class="tw-font-bold">{{ totalSelectedCompetitorCount }}/{{ selectionLimit }}</span>
            </span>

            <div class="tw-flex tw-flex-col tw-gap-4">
              <!-- Previously selected competitors are readonly -->
              <SuggestionBox
                v-for="(competitor, index) in previouslySelectedCompetitors"
                :key="index"
                :text="competitor"
                variant="purple"
                checked
                disabled
                tooltip-text="Competitors can’t be removed at this stage; you can update this after onboarding."
              />

              <SuggestionBox
                v-for="(competitor, index) in manualCompetitors"
                :key="index"
                :text="competitor"
                :checked="selectedCompetitors.has(competitor)"
                :disabled="selectedCompetitors.size >= selectionLimit && !selectedCompetitors.has(competitor)"
                :tooltip-text="tooltipText"
                @update:checked="(checked) => toggleChecked(competitor, checked)"
              />

              <SuggestionBox
                v-for="(suggestion, index) in suggestedCompetitors"
                :key="index"
                :text="suggestion.domain"
                :checked="selectedCompetitors.has(suggestion.domain)"
                :disabled="selectedCompetitors.size >= selectionLimit && !selectedCompetitors.has(suggestion.domain)"
                :tooltip-text="tooltipText"
                @update:checked="(checked) => toggleChecked(suggestion.domain, checked)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <NavigationButtons
      :loading="loading"
      @back="handleBack"
      @next="handleContinue"
    />
  </div>
</template>
